"use client";

import { useEffect, useRef } from "react";

/**
 * 2026-04-30 — generic native-<details> wrapper with optional
 * localStorage persistence. Companion to CollapsibleOrderbook
 * (which hardcodes the storage key); future cleanup pass will
 * dedupe to this generic form.
 *
 * Server-rendered HTML stays default-closed. On mount we read the
 * saved preference (when storageKey is provided) and bump
 * `details.open = true` if the user previously expanded. On every
 * toggle we write the new value back. Storage I/O wrapped in
 * try/catch so private mode + quota fall back silently.
 */
export function CollapsibleSection({
  summary,
  children,
  storageKey,
  defaultOpen = false,
  className,
}: {
  summary: React.ReactNode;
  children: React.ReactNode;
  storageKey?: string;
  defaultOpen?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved === "1") el.open = true;
        else if (saved === "0") el.open = false;
      } catch {
        // private mode / quota — silently fall back to SSR default
      }
      const onToggle = () => {
        try {
          localStorage.setItem(storageKey, el.open ? "1" : "0");
        } catch {
          // ignore
        }
      };
      el.addEventListener("toggle", onToggle);
      return () => el.removeEventListener("toggle", onToggle);
    }
  }, [storageKey]);

  return (
    <details
      ref={ref}
      open={defaultOpen}
      className={className ?? "mb-6 group"}
    >
      <summary className="mb-2 flex min-h-[40px] cursor-pointer list-none items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)] hover:text-[var(--color-text2)] md:min-h-0">
        <span className="select-none transition-transform group-open:rotate-90">
          ▶
        </span>
        {summary}
      </summary>
      {children}
    </details>
  );
}
